import { useState, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { FullscreenDialog } from '@/components/ui/fullscreen-dialog';
import { ProposalActionButtons } from '@/components/ui/proposal-action-buttons';
import type {
  PolicyProposalWithDetails,
  PolicyChangeProposal,
  Account,
  Policy,
  ExecuteAction,
} from '@mls-wallet/shared-types';
import {
  getInvalidProposalDisplayMessage,
  OrgOperationDecision,
} from '@mls-wallet/shared-types';
import {
  type PolicyWithChange,
  applyPolicyChange,
} from '../utils/policyProposalUtils';
import { PoliciesTable } from './policies-table';
import { PolicyDetailSheet } from './policy-detail-sheet';
import { PolicyChangeComparisonDialog } from './policy-change-comparison-dialog';
import { PolicyImpactPreviewDialog } from './policy-impact-preview-dialog';
import { usePolicyProposalSignature } from '@/hooks/admin/policy/use-policy-proposal-signature';
import { useExecutePolicyProposal } from '@/hooks/admin/policy/use-execute-policy-proposal';
import { usePreviewPolicyProposalImpactExisting } from '@/hooks/admin/policy/use-preview-policy-proposal-impact-existing';
import { useAuthClient } from '@/lib/authClient';
import { formatName } from '@/lib/name';
import { PendingSignaturesModal } from '@/components/modals/pending-signatures-modal';
import { useProposalExecutionState } from '@/components/execution/use-proposal-execution-state';
import { usePendingSignaturesState } from '@/hooks/admin/member/use-pending-signatures-state';
import { useGetPolicyProposal } from '@/hooks/admin/policy/use-get-policy-proposal';
import {
  getSignatureCountsAndUserVote,
  canExecuteRejection as canExecuteRejectionForProposal,
} from '@/utils/SignatureCountsHelpers';

interface ReviewPolicyChangesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  proposal: PolicyProposalWithDetails;
  policies: Policy[]; // All existing policies (for UPDATE/DELETE actions)
  accounts: Account[];
  onApplied: () => void;
  refreshProposal: () => Promise<void>;
}

export function ReviewPolicyChangesDialog({
  open,
  onOpenChange,
  proposal,
  policies,
  accounts,
  onApplied,
  refreshProposal,
}: ReviewPolicyChangesDialogProps) {
  const [selectedPolicy, setSelectedPolicy] = useState<PolicyWithChange | null>(
    null
  );
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isComparisonDialogOpen, setIsComparisonDialogOpen] = useState(false);
  const [policyForComparison, setPolicyForComparison] = useState<{
    original: Policy;
    modified: Policy;
  } | null>(null);

  // Impact preview state
  const [isImpactPreviewOpen, setIsImpactPreviewOpen] = useState(false);
  const [pendingExecuteAction, setPendingExecuteAction] =
    useState<ExecuteAction | null>(null);

  // Fetch impact preview for the existing proposal
  const { data: impactPreview } = usePreviewPolicyProposalImpactExisting(
    open && proposal.validity === 'valid' ? proposal.id : null
  );

  const {
    pendingSignableIds,
    setPendingSignableIds,
    pendingDecision,
    setPendingDecision,
    isPendingModalOpen,
    setIsPendingModalOpen,
    resetPendingState,
  } = usePendingSignaturesState();

  const { user } = useAuthClient();

  const { createSignature, isLoading: isSignatureLoading } =
    usePolicyProposalSignature({
      onError: (error) => {
        console.error('Failed to create signature', error);
        resetPendingState();
      },
    });

  const { executeProposal, isLoading: isExecuteLoading } =
    useExecutePolicyProposal();
  const { isExecuting: isProposalExecuting, startExecution } =
    useProposalExecutionState({
      proposalId: proposal.id,
      isServerExecuting: proposal.isExecuting,
      isRequestPending: isExecuteLoading,
    });

  /**
   * Handle execute button click for both approval and rejection.
   * Both APPROVE and REJECT create a GuardianTransaction and are processed via
   * the async worker. The modal polls until completion and handles callbacks.
   */
  const handleExecute = (action: ExecuteAction) => {
    // Case: an active proposal execution cannot start another request.
    if (isProposalExecuting) return;
    if (action === 'APPROVE' && !canExecute) return;
    if (action === 'REJECT' && !canExecuteRejection) return;

    // For APPROVE action, show impact preview if there are affected transactions
    if (
      action === 'APPROVE' &&
      impactPreview &&
      impactPreview.totalInvalidatedCount > 0
    ) {
      setPendingExecuteAction(action);
      setIsImpactPreviewOpen(true);
      return;
    }

    // Otherwise, execute directly
    doExecute(action);
  };

  /**
   * Actually execute the proposal (called after confirmation or if no impact).
   */
  const doExecute = (action: ExecuteAction) => {
    // Case: preview confirmation re-checks active state to block duplicate execution.
    if (isProposalExecuting) return;
    const proposalId = proposal.id;
    startExecution({
      executionTarget: 'proposal',
      executionTargetId: proposalId,
      proposalType: 'policy',
      request: () => executeProposal({ proposalId, action }),
      onAlreadyExecuting: async () => {
        await refreshProposal();
      },
      onSuccess: async () => {
        onApplied();
        onOpenChange(false);
        await refreshProposal();
      },
    });
  };

  /**
   * Handle confirmation from impact preview dialog.
   * For existing proposals, we can't undo individual changes, so excludedIds is ignored.
   */
  const handleImpactConfirm = (_excludedIds: string[]) => {
    if (pendingExecuteAction) {
      setIsImpactPreviewOpen(false);
      doExecute(pendingExecuteAction);
      setPendingExecuteAction(null);
    }
  };

  /**
   * Handle cancellation from impact preview dialog.
   */
  const handleImpactCancel = () => {
    setIsImpactPreviewOpen(false);
    setPendingExecuteAction(null);
  };

  const { numApprovals, numRejections, hasApproved, hasRejected } = useMemo(
    () =>
      getSignatureCountsAndUserVote({
        approvals: proposal?.approvals ?? [],
        rejections: proposal?.rejections ?? [],
        userId: user?.id,
        requiredNetworkIds: proposal.requiredNetworkIds,
      }),
    [proposal, user?.id]
  );

  // Check if proposal can be executed (approval)
  const canExecute = useMemo(() => {
    if (!proposal) return false;
    return (
      proposal.status === 'PENDING' &&
      numApprovals >= (proposal.requiredApprovals ?? 1)
    );
  }, [proposal, numApprovals]);

  // Check if proposal can be rejected (enough rejections)
  const canExecuteRejection = useMemo(() => {
    if (!proposal) return false;
    return canExecuteRejectionForProposal({
      status: proposal.status,
      numRejections,
      requiredApprovals: proposal.requiredApprovals,
    });
  }, [proposal, numRejections]);

  // Compute the future state of policies after applying the proposal
  const futureStatePolicies = useMemo((): PolicyWithChange[] => {
    const changes = proposal?.policyChangeProposals ?? [];

    // Build a map of policyId -> change for quick lookup
    const changesByPolicyId = new Map<string, PolicyChangeProposal>();
    const newPolicies: PolicyWithChange[] = [];

    // Process changes
    for (const change of changes) {
      if (change.action === 'CREATE') {
        // Use applyPolicyChange to properly construct the new policy
        const newPolicy = applyPolicyChange(null, change, accounts);
        newPolicies.push({ ...newPolicy, change });
      } else if (change.action === 'UPDATE' && change.policyId) {
        changesByPolicyId.set(change.policyId, change);
      } else if (change.action === 'DELETE' && change.policyId) {
        // Also track deletes in the map so they show with "Deleted" badge
        changesByPolicyId.set(change.policyId, change);
      }
    }

    // Start with existing policies in their original order
    // For UPDATE changes, apply the change to show the modified version
    // Keep deleted policies visible with DELETE change
    const result: PolicyWithChange[] = policies.map((p) => {
      const change = changesByPolicyId.get(p.id) ?? null;
      if (change && change.action === 'UPDATE') {
        // Apply the change to get the modified policy
        const modifiedPolicy = applyPolicyChange(p, change, accounts);
        return { ...modifiedPolicy, change };
      }
      return { ...p, change };
    });

    // Add new policies at the bottom
    result.push(...newPolicies);

    return result;
  }, [proposal, policies, accounts]);

  const handleRowClick = (policy: PolicyWithChange) => {
    setSelectedPolicy(policy);
    setIsSheetOpen(true);
  };

  const handleReviewChanges = (policy: PolicyWithChange) => {
    // Find the original policy and the corresponding change proposal
    const originalPolicy = policies.find((p) => p.id === policy.id);
    if (!originalPolicy || !proposal) return;

    // Find the change proposal for this policy
    const changeProposal = proposal.policyChangeProposals.find(
      (change) => change.policyId === policy.id && change.action === 'UPDATE'
    );

    if (!changeProposal) return;

    // Apply the change to get the modified policy
    const modifiedPolicy = applyPolicyChange(
      originalPolicy,
      changeProposal,
      accounts
    );

    setPolicyForComparison({
      original: originalPolicy,
      modified: modifiedPolicy,
    });
    setIsComparisonDialogOpen(true);
  };

  const handleApprove = async () => {
    try {
      // Create approval signature
      await createSignature({
        proposalId: proposal.id,
        decision: OrgOperationDecision.APPROVE,
      });
      // Refresh proposal so the frontend knows the signature record has been created, but proposal hasn't been actually signed yet
      // This way, we can close the modal when either the proposal is signed (confirmed) or the signature record is deleted (cancelled)
      await refreshProposal();
      // Open the pending signatures modal
      setPendingSignableIds([proposal.id]);
      setPendingDecision(OrgOperationDecision.APPROVE);
      setIsPendingModalOpen(true);
    } catch (error) {
      console.error('Failed to approve proposal', error);
      resetPendingState();
    }
  };

  const handleReject = async () => {
    try {
      // Create rejection signature
      await createSignature({
        proposalId: proposal.id,
        decision: OrgOperationDecision.REJECT,
      });
      // Refresh proposal so the frontend knows the signature record has been created, but proposal hasn't been actually signed yet
      // This way, we can close the modal when either the proposal is signed (confirmed) or the signature record is deleted (cancelled)
      await refreshProposal();
      // Open the pending signatures modal
      setPendingSignableIds([proposal.id]);
      setPendingDecision(OrgOperationDecision.REJECT);
      setIsPendingModalOpen(true);
    } catch (error) {
      console.error('Failed to reject proposal', error);
      resetPendingState();
    }
  };

  const footer = !proposal ? null : isSignatureLoading ? (
    <div className="flex justify-center py-4">
      <Loader2 className="h-6 w-6 animate-spin" />
    </div>
  ) : (
    <ProposalActionButtons
      onReject={handleReject}
      onApprove={handleApprove}
      onExecuteRejection={() => handleExecute('REJECT')}
      onExecute={() => handleExecute('APPROVE')}
      hasApproved={hasApproved}
      hasRejected={hasRejected}
      canExecuteRejection={canExecuteRejection}
      canExecute={canExecute}
      isExecuting={isProposalExecuting}
      approveDisabled={proposal.validity !== 'valid'}
      approveDisabledReason={
        proposal.validity !== 'valid'
          ? getInvalidProposalDisplayMessage(proposal.domainInvalidReason)
          : undefined
      }
    />
  );

  return (
    <>
      <FullscreenDialog
        open={open}
        onOpenChange={onOpenChange}
        title={`Review proposed changes to Policies`}
        noPadding
        bodyContainerClassName="bg-muted"
        footer={footer}
      >
        <div className="p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Metadata Bar */}
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center divide-x divide-border">
                <div className="pr-6">
                  <span className="text-xs font-normal text-muted-foreground">
                    Proposed by
                  </span>
                  &nbsp;&nbsp;
                  <span className="text-sm font-medium">
                    {proposal?.creator
                      ? formatName(proposal.creator.name)
                      : 'Unknown'}
                  </span>
                </div>
                <div className="px-6">
                  <span className="text-xs font-normal text-muted-foreground">
                    Proposed on
                  </span>
                  &nbsp;&nbsp;
                  <span className="text-sm font-medium">
                    {proposal?.createdAt
                      ? new Date(proposal.createdAt).toLocaleDateString(
                          'en-US',
                          {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          }
                        )
                      : '-'}
                  </span>
                </div>
                <div className="px-6">
                  <span className="text-xs font-normal text-muted-foreground">
                    Approvals
                  </span>
                  &nbsp;&nbsp;
                  <span className="text-sm font-medium">
                    {numApprovals} of {proposal?.requiredApprovals ?? 1}
                  </span>
                </div>
                <div className="pl-6">
                  <span className="text-xs font-normal text-muted-foreground">
                    Rejections
                  </span>
                  &nbsp;&nbsp;
                  <span className="text-sm font-medium">{numRejections}</span>
                </div>
              </div>
            </div>

            {/* Policies Table */}
            <PoliciesTable
              policies={futureStatePolicies}
              onRowClick={handleRowClick}
              onReviewChanges={handleReviewChanges}
              showEditStateBadges={true}
              disableEditAndRemove={true}
            />
          </div>
        </div>
      </FullscreenDialog>

      <PolicyDetailSheet
        policy={selectedPolicy}
        isOpen={isSheetOpen}
        onOpenChange={setIsSheetOpen}
        isInEditMode={false}
      />

      {policyForComparison && (
        <PolicyChangeComparisonDialog
          open={isComparisonDialogOpen}
          onOpenChange={setIsComparisonDialogOpen}
          originalPolicy={policyForComparison.original}
          modifiedPolicy={policyForComparison.modified}
        />
      )}

      {/* Pending Signature Modal */}
      <PendingSignaturesModal
        open={isPendingModalOpen}
        resetPendingState={resetPendingState}
        refreshSignables={refreshProposal}
        pendingSignableIds={pendingSignableIds}
        polledSignables={
          proposal
            ? [
                {
                  id: proposal.id,
                  approvals: proposal.approvals,
                  rejections: proposal.rejections,
                  requiredApprovals: proposal.requiredApprovals ?? 1,
                  initiatorSignature: undefined,
                },
              ]
            : []
        }
        pendingDecision={pendingDecision}
        hookFunctionToPoll={useGetPolicyProposal}
        isTransaction={false}
      />
      {/* Impact Preview Dialog - shown before executing if transactions will be invalidated */}
      <PolicyImpactPreviewDialog
        open={isImpactPreviewOpen}
        onOpenChange={setIsImpactPreviewOpen}
        impact={impactPreview ?? null}
        onConfirm={handleImpactConfirm}
        onCancel={handleImpactCancel}
        isSubmitting={isExecuteLoading}
        allowUndo={false}
      />
    </>
  );
}
